import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { generateFacetCombinations } from 'ck-utils';
import { connectHierarchicalMenu, connectStateResults } from 'react-instantsearch-dom';
import { Typography } from 'ck-ui';
import {
  trackAlgoliaClickedFilter,
  trackFacetClick
} from 'ck-utils/algoliaAnalytics';
import { Label } from '../utils';
import { FacetContainer } from './FacetContainer';
import './CategoryFacet.global.scss';
import { ProductListingPageContext } from '../context';

const Sub2Category = ({ refine, items, isMobile, indexName }) => {
  const { searchState } = useContext(ProductListingPageContext);
  const refineSubMenu = (item) => {
    if (!item.isRefined) {
      trackAlgoliaClickedFilter({
        index: indexName,
        filters: [`menu.sub2Categories:${item.label}`]
      });
      trackFacetClick({
        facet_type: 'left_menu',
        facet_name: 'menu.sub2Categories',
        facet_value: item.label,
        facet_combinations: generateFacetCombinations(searchState, 'menu.sub2Categories')
      });
    }
    refine(item.value);
  };

  return (
    <ul role="menu" className="sub-category-list">
      {items
        .filter(item => item.label !== 'null')
        .sort(Label.sort)
        .map((item, index) => item && (
          <li
            className={classnames('list-item', {
              active: item.isRefined
            })}
            role="menuitem"
            key={item.label || index}
          >
            <Typography
              capitalize
              display="inline"
              renderAs="div"
              tabIndex="0"
              className={classnames('item-link', { 'mobile-item-link': isMobile })}
              onClick={() => refineSubMenu(item)}
            >
              {item.label}
            </Typography>&nbsp;
            <Typography display="inline" className="item-link-count">({item.count})</Typography>
          </li>
        ))}
    </ul>
  );
};

Sub2Category.propTypes = {
  items: PropTypes.array.isRequired,
  refine: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  indexName: PropTypes.string,
};

Sub2Category.defaultProps = {
  indexName: undefined,
};

const SubCategory = ({ refine, items, isMobile, indexName }) => {
  const { searchState } = useContext(ProductListingPageContext);
  const refineSubMenu = (item) => {
    if (!item.isRefined) {
      trackAlgoliaClickedFilter({
        index: indexName,
        filters: [`menu.subCategories:${item.label}`]
      });
      trackFacetClick({
        facet_type: 'left_menu',
        facet_name: 'menu.subCategories',
        facet_value: item.label,
        facet_combinations: generateFacetCombinations(searchState, 'menu.subCategories')
      });
    }
    refine(item.value);
  };

  return (
    <ul role="menu" className="sub-category-list">
      {items
        .filter(item => item.label !== 'null')
        .sort(Label.sort)
        .map((item, index) => item && (
          <>
            <li
              className={classnames('list-item', {
                active: item.isRefined
              })}
              role="menuitem"
              key={item.label || index}
            >
              <Typography
                capitalize
                display="inline"
                renderAs="div"
                tabIndex="0"
                className={classnames('item-link', { 'mobile-item-link': isMobile })}
                onClick={() => refineSubMenu(item)}
              >
                {Label.getLocaleString(item.label, 'subcategory')}
              </Typography>&nbsp;
              <Typography display="inline" className="item-link-count">({item.count})</Typography>
            </li>
            {
              item.isRefined && item.items && (
                <Sub2Category
                  refine={refine}
                  items={item.items}
                  index={index}
                  isMobile={isMobile}
                />
              )
            }
          </>
        ))}
    </ul>
  );
};

SubCategory.propTypes = {
  items: PropTypes.array.isRequired,
  refine: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  indexName: PropTypes.string,
};

SubCategory.defaultProps = {
  indexName: undefined,
};

const Category = ({ refine, items, searchResults, ...rest }) => {
  const { isMobile, searchState } = useContext(ProductListingPageContext);
  const refineMenu = (item) => {
    if (!item.isRefined) {
      trackAlgoliaClickedFilter({
        index: searchResults?.index,
        filters: [`menu.categories:${item.label}`]
      });
      trackFacetClick({
        facet_type: 'left_menu',
        facet_name: 'menu.categories',
        facet_value: item.value,
        facet_combinations: generateFacetCombinations(searchState, 'menu.categories')
      });
    }
    refine(item.value);
  };

  return (
    <ul role="menu" className={classnames('category-list', { 'mobile-sub-category-list': isMobile })}>
      {items
        .sort(Label.sort)
        .map((item, index) => item && (
          <React.Fragment key={item.label || index}>
            <li
              role="menuitem"
              className={classnames('list-item', {
                active: item.isRefined
              })}
            >
              <Typography
                capitalize
                color="black-bis"
                display="inline"
                renderAs="div"
                tabIndex="0"
                className={classnames('item-link', { 'mobile-item-link': isMobile })}
                weight={500}
                onClick={() => refineMenu(item)}
              >
                {Label.getLocaleString(item.label, 'category')}
              </Typography>&nbsp;
              <Typography
                display="inline"
                color={item.isRefined ? 'white' : null}
                className="item-link-count"
              >({item.count})
              </Typography>
            </li>
            {
              item.isRefined && item.items && (
                <SubCategory
                  {...rest}
                  refine={refine}
                  items={item.items}
                  index={index}
                  isMobile={isMobile}
                />
              )
            }
          </React.Fragment>
        )) }
    </ul>
  );
};

Category.propTypes = {
  items: PropTypes.array.isRequired,
  refine: PropTypes.func.isRequired,
  searchResults: PropTypes.object,
};

Category.defaultProps = {
  searchResults: null
};

const ConnectedMenu = connectHierarchicalMenu(connectStateResults(Category));

const CategoryFacet = () => (
  <div className="menu-widget-container facet">
    <FacetContainer
      forceRender
      label="Categories"
      menu
      render={() => (
        <ConnectedMenu
          attributes={['menu.categories', 'menu.subCategories', 'menu.sub2Categories']}
          limit={100}
        />
      )}
    />
  </div>
);

export { CategoryFacet };
